/**
 * Iter-157B Phase 4 — Archive Integrity surfaces.
 *
 * Trust contract:
 *   Archive Integrity is PROOF, not REPAIR. The UI never offers to
 *   "heal" or "fix" a mismatch.
 *
 * Voice contract (after 2026-02 UX refinement):
 *   Most archive readers are incoming HODs, researchers, replacement
 *   crew — people who weren't there when the archive was created and
 *   should never be made to feel responsible for "fixing" a dusty box
 *   on a shelf. So we lead with EXPLANATION, not ALARM:
 *     • Headline: "Something needs attention" — not "Integrity mismatch".
 *     • Body: one sentence in plain English explaining WHAT changed
 *       and a second sentence reassuring that the rest of the archive
 *       is still readable.
 *     • Acknowledgement is framed as "make a note and continue" —
 *       not "override a security failure".
 *
 *   Catastrophic loss (currently undetectable in this layer) would
 *   warrant alarm copy; the current verification surface only catches
 *   drift / additions / missing rows, which read calmly by default.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ShieldCheck,
    ShieldQuestion,
    Loader2,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Microscope,
    X,
    PenLine,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";


function relTime(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        if (diff < 0) return "just now";
        const m = Math.floor(diff / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 30) return `${d}d ago`;
        const mo = Math.floor(d / 30);
        if (mo < 12) return `${mo}mo ago`;
        return `${Math.floor(mo / 12)}y ago`;
    } catch (_) { return "—"; }
}


function formatExact(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_) { return iso; }
}


/**
 * Compute the chip state from a manifest payload.
 *
 * Three states only — and crucially, the "amber" tone is the one we
 * use for ANY mismatch. We do NOT use red unless the verification
 * engine ever surfaces catastrophic loss (it doesn't today). The
 * archive is intact; something just needs attention.
 */
function deriveChipState(manifest) {
    const verifs = (manifest?.verifications || []);
    if (verifs.length === 0) {
        return {
            tone: "amber",
            label: "Not yet checked",
            detail: "Run a check to confirm this archive is intact.",
        };
    }
    const latest = verifs[verifs.length - 1];
    if (latest.result === "verified") {
        return {
            tone: "green",
            label: "Archive intact",
            detail: `Last checked ${relTime(latest.verified_at)}.`,
        };
    }
    const n = latest.mismatch_count || 0;
    return {
        tone: "amber",
        label: "Needs attention",
        detail: `${n} item${n === 1 ? "" : "s"} differ${n === 1 ? "s" : ""} from the original archive.`,
    };
}


// --------------------------------------------------------------------
// DeepCheckReport — plain-English explainer when a Detailed check
// reports anything other than "intact". Replaces the previous opaque
// "open the activity log" message. The trust contract says: never
// show generic failure — say what failed, why it failed, and what to
// do next.
// --------------------------------------------------------------------


const _DEEP_BUCKET_LABELS = {
    media: "Continuity photos",
    change_documents: "Uploaded documents",
    documents: "Uploaded documents",
};

function _deepFindingDescription(finding) {
    const bucket = _DEEP_BUCKET_LABELS[finding.bucket] || finding.bucket || "Files";
    if (finding.issue === "read_error") {
        return {
            label: bucket,
            what: "couldn’t be read from storage.",
            why: `Storage layer error: ${(finding.detail || "unknown").slice(0, 140)}`,
            next: "Owner: try the Detailed check again in a few minutes. If it keeps reporting the same file, the underlying file may be unrecoverable — contact support before doing anything destructive.",
        };
    }
    if (finding.issue === "length_mismatch") {
        return {
            label: bucket,
            what: `is a different size than when it was sealed (expected ${finding.expected ?? "—"} B, found ${finding.observed ?? "—"} B).`,
            why: "The file in storage doesn’t match the original. This usually means it was re-uploaded, edited externally, or the storage layer truncated it.",
            next: "Owner: compare the file with your off-site backup. If the current version is correct, make a note and continue. If not, restore from your backup before unarchiving.",
        };
    }
    return {
        label: bucket,
        what: `flagged a ${String(finding.issue || "difference").replace(/_/g, " ")}.`,
        why: finding.detail || "No further detail available.",
        next: "Owner: review the file. If the current version is the intended one, make a note and continue.",
    };
}


function DeepCheckReport({ deepRun }) {
    const findings = deepRun?.blob_pass?.blob_findings || deepRun?.blob_findings || [];
    const quick = deepRun?.quick || null;
    const quickFailed = quick && quick.result !== "verified";
    const status = deepRun?.status;

    if (status === "verified") {
        return (
            <p
                data-testid="archive-integrity-deep-success"
                className="mt-2 text-[11px] text-emerald-500/90"
            >
                Detailed check finished. Every file in the archive matches its
                original size and content.
            </p>
        );
    }
    if (status === "cancelled") {
        return (
            <p
                data-testid="archive-integrity-deep-cancelled"
                className="mt-2 text-[11px] text-neutral-400"
            >
                Detailed check was stopped before it finished. You can run it
                again any time.
            </p>
        );
    }
    if (status === "errored") {
        return (
            <p
                data-testid="archive-integrity-deep-errored"
                className="mt-2 text-[11px] text-amber-700 dark:text-amber-200"
            >
                The detailed check couldn’t finish. This is usually a transient
                storage hiccup — try again in a minute. If it keeps happening,
                contact support and quote the timestamp above.
            </p>
        );
    }

    // status === "failed" → explain everything.
    if (!quickFailed && findings.length === 0) {
        return (
            <p
                data-testid="archive-integrity-deep-failed-empty"
                className="mt-2 text-[11px] text-amber-700 dark:text-amber-200"
            >
                The detailed check completed but reported a failure without
                attaching findings. This is unusual — please run it once more,
                and if it repeats, contact support with this timestamp.
            </p>
        );
    }

    return (
        <div
            data-testid="archive-integrity-deep-findings"
            className="mt-2 space-y-2"
        >
            <p className="text-amber-700 dark:text-amber-100/95 font-medium">
                The detailed check found {findings.length + (quickFailed ? quick.mismatch_count : 0)} thing
                {(findings.length + (quickFailed ? quick.mismatch_count : 0)) === 1 ? "" : "s"} worth a closer look.
            </p>

            {/* Quick-pass mismatches inside the deep run (rare but
                possible — e.g. a row changed between the manual quick
                check and the detailed check). */}
            {quickFailed && (quick.mismatches || []).length > 0 ? (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.04] p-2.5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-200 mb-1.5">
                        Project data
                    </p>
                    <ul className="space-y-1 text-[11px] text-neutral-200">
                        {(quick.mismatches || []).map((m, i) => (
                            <li
                                key={`q-${i}`}
                                data-testid={`archive-integrity-deep-quick-row-${i}`}
                                className="leading-relaxed"
                            >
                                {m.summary || `${m.label || m.surface}: differs from the original.`}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* File-level findings — the meat of the detailed check. */}
            {findings.length > 0 ? (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.04] divide-y divide-amber-500/20">
                    {findings.slice(0, 30).map((f, idx) => {
                        const d = _deepFindingDescription(f);
                        return (
                            <div
                                key={`b-${idx}`}
                                data-testid={`archive-integrity-deep-finding-${idx}`}
                                className="px-3 py-2 text-[11px] leading-relaxed text-neutral-200"
                            >
                                <p>
                                    <span className="font-semibold text-amber-700 dark:text-amber-100">
                                        {d.label}
                                    </span>{" "}
                                    {d.what}
                                </p>
                                <p className="text-neutral-400 mt-0.5">
                                    <span className="text-neutral-500">Why · </span>
                                    {d.why}
                                </p>
                                <p className="text-neutral-400">
                                    <span className="text-neutral-500">What to do · </span>
                                    {d.next}
                                </p>
                                {f.file_id ? (
                                    <p className="text-[10px] mono text-neutral-500 mt-1 break-all">
                                        ref: {f.file_id}
                                    </p>
                                ) : null}
                            </div>
                        );
                    })}
                    {findings.length > 30 ? (
                        <div className="px-3 py-2 text-[10px] text-neutral-500">
                            …and {findings.length - 30} more file
                            {findings.length - 30 === 1 ? "" : "s"} in the audit
                            log.
                        </div>
                    ) : null}
                </div>
            ) : null}

            <p className="text-[11px] text-neutral-500 leading-relaxed">
                The archive itself hasn’t been changed by this check. The
                report above is read-only.
            </p>
        </div>
    );
}


// --------------------------------------------------------------------
// Compact chip badge
// --------------------------------------------------------------------


function ChipBadge({ tone, label, testid }) {
    const palette = {
        green: "border-emerald-500/40 bg-emerald-500/[0.08] text-emerald-700 dark:text-emerald-100/90",
        amber: "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-100/90",
    }[tone];
    const Icon = tone === "green" ? ShieldCheck : ShieldQuestion;
    const iconColour = {
        green: "text-emerald-600 dark:text-emerald-300",
        amber: "text-amber-600 dark:text-amber-300",
    }[tone];
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-md border ${palette} px-2.5 py-1 text-[11px] font-medium`}
        >
            <Icon size={11} className={iconColour} />
            {label}
        </span>
    );
}


// --------------------------------------------------------------------
// Owner "make a note and continue" dialog (formerly "Acknowledge")
// --------------------------------------------------------------------


export function ArchiveIntegrityAckDialog({
    open, onClose, projectId, projectName, observedHash, onAcknowledged,
}) {
    const [reason, setReason] = useState("");
    const [confirmName, setConfirmName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) { setReason(""); setConfirmName(""); }
    }, [open]);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post(
                `/projects/${projectId}/archive-integrity/acknowledge`,
                {
                    reason: reason.trim(),
                    confirm_name: confirmName.trim(),
                    manifest_hash_observed: observedHash || null,
                },
            );
            if (res.data?.acknowledged) {
                toast.success("Note saved. You can continue.");
            } else {
                toast.success("All good — the archive checks out.");
            }
            onAcknowledged?.(res.data);
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err, "Could not save your note."));
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit =
        confirmName.trim() === (projectName || "").trim()
        && reason.trim().length >= 4
        && !submitting;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
            <DialogContent data-testid="archive-integrity-ack-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PenLine size={16} className="text-amber-400" />
                        Make a note &amp; continue
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400 text-sm leading-relaxed">
                        Leave a short note so future-you (or the next HOD)
                        knows the differences were expected. The archive itself
                        is not changed — your note becomes part of its history.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
                            What does future-you need to know?
                        </label>
                        <Textarea
                            data-testid="archive-integrity-ack-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. The missing photo was the duplicate that we cleaned up after wrap."
                            rows={3}
                            maxLength={1000}
                            disabled={submitting}
                            className="resize-none"
                        />
                        <p className="mt-1 text-[11px] text-neutral-500">
                            This note is kept with the archive forever.
                        </p>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
                            Type the project name to confirm
                        </label>
                        <Input
                            data-testid="archive-integrity-ack-confirm-name"
                            value={confirmName}
                            onChange={(e) => setConfirmName(e.target.value)}
                            placeholder={projectName}
                            disabled={submitting}
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => !submitting && onClose?.()}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        data-testid="archive-integrity-ack-submit"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="font-semibold"
                    >
                        {submitting ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                Saving…
                            </span>
                        ) : "Save note & continue"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// --------------------------------------------------------------------
// Plain-English mismatch report — leads with the summary line, hides
// the forensic hashes behind a "show technical details" toggle.
// --------------------------------------------------------------------


function MismatchReport({ mismatches }) {
    const [showTech, setShowTech] = useState(false);
    if (!mismatches || mismatches.length === 0) return null;
    return (
        <div
            data-testid="archive-integrity-mismatch-report"
            className="rounded-md border border-amber-500/30 bg-amber-500/[0.04] overflow-hidden"
        >
            <div className="px-3 py-2 border-b border-amber-500/30 text-[11px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-100/90 flex items-center justify-between gap-2">
                <span>What changed</span>
                <button
                    type="button"
                    data-testid="archive-integrity-mismatch-tech-toggle"
                    onClick={() => setShowTech((v) => !v)}
                    className="normal-case tracking-normal text-[10px] text-amber-600 hover:text-amber-500 dark:text-amber-200/80"
                >
                    {showTech ? "Hide technical details" : "Show technical details"}
                </button>
            </div>
            <div className="divide-y divide-amber-500/20">
                {mismatches.map((m, idx) => (
                    <div key={idx} className="px-3 py-2.5" data-testid={`mismatch-row-${idx}`}>
                        <p className="text-xs text-neutral-200 leading-relaxed">
                            {m.summary || `${m.label || m.surface}: differs from the original archive.`}
                        </p>
                        {showTech ? (
                            <div className="mt-1.5 grid grid-cols-2 gap-2 text-[10px] text-neutral-500 font-mono">
                                <div>
                                    <span className="text-neutral-500">surface · </span>
                                    {m.surface}
                                </div>
                                <div>
                                    <span className="text-neutral-500">kind · </span>
                                    {String(m.kind || "").replace(/_/g, " ")}
                                </div>
                                <div>
                                    <span className="text-neutral-500">expected · </span>
                                    {m.expected?.count ?? "—"}
                                    {m.expected?.total_bytes != null ? `, ${m.expected.total_bytes} B` : ""}
                                </div>
                                <div>
                                    <span className="text-neutral-500">observed · </span>
                                    {m.observed?.count ?? "—"}
                                    {m.observed?.total_bytes != null ? `, ${m.observed.total_bytes} B` : ""}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
            <div className="px-3 py-2 bg-amber-500/[0.03] text-[11px] text-neutral-400 leading-relaxed border-t border-amber-500/20">
                Everything else in this archive is intact and accessible. You
                can keep using it as normal.
            </div>
        </div>
    );
}


// --------------------------------------------------------------------
// ArchiveIntegrityChip — compact surface for The Attic
// --------------------------------------------------------------------


export function ArchiveIntegrityChip({ projectId, projectName, role }) {
    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showAck, setShowAck] = useState(false);
    const [latestMismatches, setLatestMismatches] = useState([]);
    const [error, setError] = useState("");

    const fetchManifest = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const r = await api.get(`/projects/${projectId}/archive-integrity`);
            setManifest(r.data || null);
            const verifs = r.data?.verifications || [];
            const latest = verifs[verifs.length - 1];
            if (latest && latest.result !== "verified" && latest.mismatches) {
                setLatestMismatches(latest.mismatches);
            } else {
                setLatestMismatches([]);
            }
        } catch (err) {
            setError(formatApiError(err, "Could not load archive check."));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { fetchManifest(); }, [fetchManifest]);

    const chip = useMemo(() => deriveChipState(manifest), [manifest]);

    const handleVerify = async () => {
        setVerifying(true);
        try {
            const r = await api.post(`/projects/${projectId}/archive-integrity/verify`);
            const result = r.data;
            if (result.result === "verified") {
                toast.success("Archive intact. Everything checks out.");
                setLatestMismatches([]);
            } else {
                const n = result.mismatch_count || 0;
                toast(`Archive check finished — ${n} item${n === 1 ? "" : "s"} differ${n === 1 ? "s" : ""} from the original.`);
                setLatestMismatches(result.mismatches || []);
                setExpanded(true);
            }
            await fetchManifest();
        } catch (err) {
            toast.error(formatApiError(err, "Could not run the check."));
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <span
                data-testid="archive-integrity-chip-loading"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1 text-[11px] text-neutral-400"
            >
                <Loader2 size={11} className="animate-spin" />
                Checking archive…
            </span>
        );
    }
    if (error) {
        return (
            <span
                data-testid="archive-integrity-chip-error"
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-2.5 py-1 text-[11px] text-amber-700 dark:text-amber-200"
            >
                <ShieldQuestion size={11} /> Archive check unavailable
            </span>
        );
    }
    if (!manifest) return null;

    const latest = (manifest.verifications || []).slice(-1)[0];
    const observedHash = latest?.manifest_hash_observed || manifest.manifest_hash;
    const hasReport = chip.tone !== "green" && latestMismatches.length > 0;

    return (
        <div className="inline-flex flex-col gap-2 max-w-2xl" data-testid="archive-integrity-chip">
            <div className="flex flex-wrap items-center gap-2">
                <ChipBadge tone={chip.tone} label={chip.label} testid="archive-integrity-chip-badge" />
                <span className="text-[11px] text-neutral-400" data-testid="archive-integrity-chip-detail">
                    {chip.detail}
                </span>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="archive-integrity-verify-now"
                    onClick={handleVerify}
                    disabled={verifying}
                    className="text-[11px] h-7 px-2 text-neutral-300"
                >
                    {verifying ? (
                        <span className="inline-flex items-center gap-1.5">
                            <Loader2 size={11} className="animate-spin" /> Checking…
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5">
                            <RefreshCw size={11} /> Check again
                        </span>
                    )}
                </Button>
                {hasReport ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        data-testid="archive-integrity-expand-toggle"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-[11px] h-7 px-2 text-amber-600 dark:text-amber-300"
                    >
                        {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                        {expanded ? "Hide details" : "See what changed"}
                    </Button>
                ) : null}
            </div>

            {expanded && hasReport ? (
                <div className="mt-1 space-y-2.5">
                    <p className="text-[12px] text-neutral-300 leading-relaxed">
                        Archive verification found something that may need a quick
                        review. The archive itself is unchanged and still readable.
                    </p>
                    <MismatchReport mismatches={latestMismatches} />
                    {role === "owner" ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                data-testid="archive-integrity-open-ack"
                                onClick={() => setShowAck(true)}
                                className="font-semibold"
                            >
                                Make a note &amp; continue
                            </Button>
                            <span className="text-[11px] text-neutral-500 leading-snug">
                                Your note is kept with the archive. The archive itself isn’t changed.
                            </span>
                        </div>
                    ) : (
                        <p className="text-[11px] text-neutral-500">
                            The project owner can leave a note explaining the
                            difference. You can keep using the archive in the
                            meantime.
                        </p>
                    )}
                </div>
            ) : null}

            <ArchiveIntegrityAckDialog
                open={showAck}
                onClose={() => setShowAck(false)}
                projectId={projectId}
                projectName={projectName}
                observedHash={observedHash}
                onAcknowledged={() => fetchManifest()}
            />
        </div>
    );
}


// --------------------------------------------------------------------
// ArchiveIntegrityCard — full Project Settings surface
// --------------------------------------------------------------------


export function ArchiveIntegrityCard({ projectId, projectName, role }) {
    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const [deepRun, setDeepRun] = useState(null);
    const [deepStarting, setDeepStarting] = useState(false);
    const [showAck, setShowAck] = useState(false);

    const fetchManifest = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const r = await api.get(`/projects/${projectId}/archive-integrity`);
            setManifest(r.data || null);
        } catch (err) {
            setError(formatApiError(err, "Could not load archive check."));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { fetchManifest(); }, [fetchManifest]);

    useEffect(() => {
        if (!deepRun?.deep_id) return undefined;
        if (["verified", "failed", "errored", "cancelled"].includes(deepRun.status)) {
            return undefined;
        }
        let alive = true;
        const tick = async () => {
            try {
                const r = await api.get(
                    `/projects/${projectId}/archive-integrity/verify-deep/${deepRun.deep_id}`
                );
                if (!alive) return;
                setDeepRun(r.data);
                if (["verified", "failed"].includes(r.data?.status)) {
                    fetchManifest();
                }
            } catch (_) { /* swallow polling errors */ }
        };
        const handle = setInterval(tick, 1200);
        return () => { alive = false; clearInterval(handle); };
    }, [deepRun?.deep_id, deepRun?.status, projectId, fetchManifest]);

    const chip = useMemo(() => deriveChipState(manifest), [manifest]);

    const handleVerify = async () => {
        setVerifying(true);
        try {
            const r = await api.post(`/projects/${projectId}/archive-integrity/verify`);
            if (r.data?.result === "verified") {
                toast.success("Archive intact.");
            } else {
                const n = r.data?.mismatch_count || 0;
                toast(`Check finished — ${n} item${n === 1 ? "" : "s"} differ${n === 1 ? "s" : ""} from the original.`);
            }
            await fetchManifest();
        } catch (err) {
            toast.error(formatApiError(err, "Could not run the check."));
        } finally {
            setVerifying(false);
        }
    };

    const handleDeepVerify = async () => {
        setDeepStarting(true);
        try {
            const r = await api.post(`/projects/${projectId}/archive-integrity/verify-deep`);
            setDeepRun({ ...r.data, status: "pending" });
            toast.success("Detailed check started.");
        } catch (err) {
            toast.error(formatApiError(err, "Could not start detailed check."));
        } finally {
            setDeepStarting(false);
        }
    };

    const handleDeepCancel = async () => {
        if (!deepRun?.deep_id) return;
        try {
            await api.post(`/projects/${projectId}/archive-integrity/verify-deep/${deepRun.deep_id}/cancel`);
            toast.success("Detailed check stopped.");
        } catch (err) {
            toast.error(formatApiError(err, "Could not stop the check."));
        }
    };

    if (loading) {
        return (
            <div
                data-testid="archive-integrity-card-loading"
                className="rounded-md border border-white/10 bg-card p-4 text-sm text-neutral-400 inline-flex items-center gap-2"
            >
                <Loader2 size={14} className="animate-spin" />
                Checking the archive…
            </div>
        );
    }
    if (error || !manifest) {
        return (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] p-4 text-sm text-amber-700 dark:text-amber-200">
                {error || "This archive hasn’t been fingerprinted yet — open it once as Owner / Admin to capture a baseline."}
            </div>
        );
    }

    const verifs = manifest.verifications || [];
    const latest = verifs[verifs.length - 1];
    const observedHash = latest?.manifest_hash_observed || manifest.manifest_hash;
    const deepRunning = deepRun && ["pending", "running"].includes(deepRun.status);
    const deepFinal = deepRun && ["verified", "failed", "errored", "cancelled"].includes(deepRun.status);
    const hasMismatchReport = latest && latest.result !== "verified" && (latest.mismatches || []).length > 0;

    return (
        <div
            data-testid="archive-integrity-card"
            className="rounded-xl border border-white/10 bg-card overflow-hidden"
        >
            <header className="px-4 py-3 border-b border-white/10 flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-neutral-100 inline-flex items-center gap-2">
                        <ShieldCheck size={14} className="text-emerald-400" />
                        Archive check
                    </h3>
                    <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed max-w-md">
                        A quiet integrity check confirms this archive still
                        matches what was sealed at wrap. Run it any time.
                    </p>
                </div>
                <ChipBadge tone={chip.tone} label={chip.label} testid="archive-integrity-card-badge" />
            </header>

            {/* Calm explanatory banner when there's a mismatch — leads
                the surface ABOVE the metadata grid. */}
            {hasMismatchReport ? (
                <div
                    data-testid="archive-integrity-card-banner"
                    className="px-4 py-3 border-b border-amber-500/30 bg-amber-500/[0.04] text-sm text-neutral-200 leading-relaxed"
                >
                    <p className="font-medium text-amber-700 dark:text-amber-100/95">
                        Something in this archive needs attention.
                    </p>
                    <p className="text-[12px] text-neutral-400 mt-1">
                        The archive is still readable. The check found{" "}
                        {latest.mismatch_count} difference
                        {latest.mismatch_count === 1 ? "" : "s"} compared to the
                        original wrap snapshot — see below.
                    </p>
                </div>
            ) : null}

            <div className="p-4 space-y-3 text-xs">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-neutral-400">
                    <dt className="text-neutral-500">Sealed at wrap</dt>
                    <dd data-testid="archive-integrity-archived-at">
                        {formatExact(manifest.archived_at)}
                    </dd>
                    <dt className="text-neutral-500">Fingerprint</dt>
                    <dd className="font-mono text-[10px] text-neutral-300 break-all">
                        {(manifest.manifest_hash || "").slice(0, 30)}…
                    </dd>
                    <dt className="text-neutral-500">Last checked</dt>
                    <dd data-testid="archive-integrity-last-verified">
                        {latest
                            ? `${formatExact(latest.verified_at)} · ${latest.result === "verified" ? "intact" : "needs attention"}`
                            : "Never"}
                    </dd>
                    {manifest.back_filled ? (
                        <>
                            <dt className="text-neutral-500">Baseline source</dt>
                            <dd className="text-amber-700 dark:text-amber-200">
                                Captured later (current state)
                            </dd>
                        </>
                    ) : null}
                </dl>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                        type="button"
                        size="sm"
                        data-testid="archive-integrity-card-verify"
                        onClick={handleVerify}
                        disabled={verifying}
                        className="font-semibold"
                    >
                        {verifying ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={12} className="animate-spin" />
                                Checking…
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-2">
                                <RefreshCw size={12} /> Check archive
                            </span>
                        )}
                    </Button>
                    {role === "owner" ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="archive-integrity-deep-verify"
                            onClick={handleDeepVerify}
                            disabled={deepStarting || deepRunning}
                            className="font-semibold"
                        >
                            <span className="inline-flex items-center gap-2">
                                <Microscope size={12} />
                                {deepRunning ? "Detailed check running…" : "Detailed check"}
                            </span>
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        data-testid="archive-integrity-history-toggle"
                        onClick={() => setShowHistory((v) => !v)}
                        className="text-neutral-300"
                    >
                        {showHistory ? "Hide history" : `Show history (${verifs.length})`}
                    </Button>
                </div>

                {/* Detailed (deep) check progress / result */}
                {deepRun ? (
                    <div
                        data-testid="archive-integrity-deep-progress"
                        className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-[11px] text-neutral-300"
                    >
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="inline-flex items-center gap-2">
                                <Microscope size={11} className="text-emerald-400" />
                                Detailed check ·{" "}
                                <span data-testid="archive-integrity-deep-status">
                                    {deepRun.status}
                                </span>
                            </span>
                            {deepRunning && role === "owner" ? (
                                <button
                                    type="button"
                                    data-testid="archive-integrity-deep-cancel"
                                    onClick={handleDeepCancel}
                                    className="text-neutral-400 hover:text-neutral-200 inline-flex items-center gap-1"
                                >
                                    <X size={11} /> Stop
                                </button>
                            ) : null}
                        </div>
                        <div className="text-neutral-400">
                            {deepRun.processed_blobs || 0} / {deepRun.total_blobs || 0} files
                            {deepRun.total_bytes_hashed ? (
                                <> · {(deepRun.total_bytes_hashed / 1024).toFixed(1)} KB read</>
                            ) : null}
                        </div>
                        {/* Iter-157C — surface deep-check findings inline.
                            The user must NEVER see a generic "failed" with
                            no explanation. Trust depends on knowing what
                            went wrong and what to do next. */}
                        {deepFinal ? (
                            <DeepCheckReport deepRun={deepRun} />
                        ) : null}
                    </div>
                ) : null}

                {/* Mismatch report for the latest verification */}
                {hasMismatchReport ? (
                    <div className="space-y-2">
                        <MismatchReport mismatches={latest.mismatches} />
                        {role === "owner" ? (
                            <Button
                                type="button"
                                size="sm"
                                data-testid="archive-integrity-card-open-ack"
                                onClick={() => setShowAck(true)}
                                className="font-semibold"
                            >
                                Make a note &amp; continue
                            </Button>
                        ) : (
                            <p className="text-[11px] text-neutral-500">
                                The project owner can leave a note explaining
                                the difference. You can keep using the archive.
                            </p>
                        )}
                    </div>
                ) : null}

                {/* Verification history sheet */}
                {showHistory ? (
                    <div
                        data-testid="archive-integrity-history-sheet"
                        className="rounded-md border border-white/10 overflow-hidden"
                    >
                        {verifs.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-neutral-500">
                                No checks yet.
                            </div>
                        ) : (
                            <div className="divide-y divide-white/10">
                                {[...verifs].reverse().map((v, idx) => (
                                    <div
                                        key={idx}
                                        data-testid={`archive-integrity-history-row-${idx}`}
                                        className="px-3 py-2 text-[11px] flex items-center justify-between gap-2"
                                    >
                                        <div>
                                            <div className="text-neutral-300">
                                                {formatExact(v.verified_at)}{" "}
                                                <span className="text-neutral-500">· {v.mode || "quick"}</span>
                                            </div>
                                            <div className="text-neutral-500">
                                                {v.verified_by_name || "—"}
                                                {v.mismatch_count
                                                    ? ` · ${v.mismatch_count} difference${v.mismatch_count === 1 ? "" : "s"}`
                                                    : ""}
                                            </div>
                                        </div>
                                        <ChipBadge
                                            tone={v.result === "verified" ? "green" : "amber"}
                                            label={v.result === "verified" ? "intact" : "needs attention"}
                                            testid={`archive-integrity-history-result-${idx}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>

            <ArchiveIntegrityAckDialog
                open={showAck}
                onClose={() => setShowAck(false)}
                projectId={projectId}
                projectName={projectName}
                observedHash={observedHash}
                onAcknowledged={() => fetchManifest()}
            />
        </div>
    );
}
